import { memo, useContext } from 'react';
import styled from '@emotion/styled';

import { getInitials } from '../../../../lib/getInitials';
import { themeFont } from '../../../../lib/themeFont';
import { useCurrentRoute } from '../../../../lib/useCurrentRoute';
import { WWContext, type WWContextValue } from '../../../WWContext';
import useThemeStyles from '../../../../lib/useThemeStyles';

import {
  isWwsWeddingWebsiteV2,
  type Wws_Page,
} from '../../../../types/graphql';
import InlineEditHandler from '../../../InlineEdit/InlineEditHandler/InlineEditHandler';
import { buildEditPageTitleContextMenuButtons } from '../../../InlineEdit/InlineEditMessageFactory';
import { getCoupleNames } from '../../../../lib/getCoupleNames';

const TitleInitials = styled('h2')`
  flex: 1;
  text-align: center;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  padding: 4px 0;
  text-overflow: ellipsis;

  ${themeFont('s3')}
`;

export interface MobilePageTitleProps {
  pages: Partial<Wws_Page>[] | undefined;
  preferPageTitle?: boolean | undefined;
}

function MobilePageTitle({ pages, preferPageTitle }: MobilePageTitleProps) {
  const themeStyles = useThemeStyles();
  const { data, isSinglePageLayout } = useContext<WWContextValue>(WWContext);
  const currentRoute = useCurrentRoute();

  if (!isWwsWeddingWebsiteV2(data?.weddingWebsiteResult)) {
    return null;
  }

  const currentPage = pages?.find((page) => page.routeName === currentRoute);

  if (!currentPage || themeStyles.config?.hideMobileInitials) {
    return null;
  }

  const [primaryName, secondaryName] = getCoupleNames({ weddingWebsiteResult: data?.weddingWebsiteResult }) ?? [];
  const initials = getInitials({
    firstName: primaryName || '',
    fianceFirstName: secondaryName || '',
  });

  const preferInitials =
    currentPage.routeName === '' || (isSinglePageLayout && !preferPageTitle);

  let title = currentPage.title;

  if (preferInitials && Boolean(initials)) {
    title = initials;
  }

  // Hiding the handler until ios has an event interceptor for editing page titles built
  const editModeOverride = 'forceHide'; // preferInitials ? 'forceHide' : 'default'

  return (
    <InlineEditHandler
      contextMenuButtons={buildEditPageTitleContextMenuButtons(
        currentPage as Wws_Page
      )}
      editModeOverride={editModeOverride}
    >
      <TitleInitials data-testid="mobile-page-title">
        {title}
      </TitleInitials>{' '}
    </InlineEditHandler>
  );
}

export default memo(MobilePageTitle);
